import { useState, useCallback, useRef } from 'react';
import { consultationService } from '../../../services/consultationService';
import type { ConsultationFilters } from '../../../services/consultationService';
import type { MedicalConsultation } from '../../../types/api';

export const useConsultationList = () => {
  const [consultations, setConsultations] = useState<MedicalConsultation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ConsultationFilters>({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 15,
  });
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const fetchConsultations = useCallback(async (page = 1, currentFilters?: ConsultationFilters) => {
    setLoading(true);
    setError(null);
    try {
      const appliedFilters = currentFilters ?? filters;
      const response = await consultationService.getAll({
        ...appliedFilters,
        page,
        per_page: pagination.perPage,
      });
      setConsultations(response.data || []);
      setPagination(prev => ({
        ...prev,
        currentPage: response.current_page || page,
        totalPages: response.last_page || 1,
        total: response.total || 0,
      }));
    } catch (err) {
      console.error('Error fetching consultations:', err);
      setError('Error al cargar las consultas');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.perPage]);

  const updateFilters = useCallback((newFilters: Partial<ConsultationFilters>) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      fetchConsultations(1, updated);
      return updated;
    });
  }, [fetchConsultations]);

  const updateSearch = useCallback((search: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateFilters({ search: search || undefined });
    }, 400);
  }, [updateFilters]);

  const clearFilters = useCallback(() => {
    setFilters({});
    fetchConsultations(1, {});
  }, [fetchConsultations]);

  const fetchPatientHistory = useCallback(async (patientId: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await consultationService.getPatientHistory(patientId);
      setConsultations(data || []);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        totalPages: 1,
        total: data?.length || 0,
      }));
    } catch (err) {
      console.error('Error fetching patient history:', err);
      setError('Error al cargar el historial del paciente');
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteConsultation = useCallback(async (id: number) => {
    if (!confirm('¿Está seguro de eliminar esta consulta?')) return;
    setLoading(true);
    try {
      await consultationService.delete(id);
      await fetchConsultations(pagination.currentPage);
    } catch (err) {
      console.error('Error deleting consultation:', err);
      setError('Error al eliminar la consulta');
    } finally {
      setLoading(false);
    }
  }, [fetchConsultations, pagination.currentPage]);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchConsultations(page);
    }
  }, [fetchConsultations, pagination.totalPages]);

  return {
    consultations,
    loading,
    error,
    filters,
    pagination,
    fetchConsultations,
    fetchPatientHistory,
    deleteConsultation,
    goToPage,
    updateFilters,
    updateSearch,
    clearFilters,
  };
};
